import {Component, Inject, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import * as moment from 'moment';
import {MatDatepickerInputEvent} from '@angular/material/datepicker';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {forkJoin, of} from 'rxjs';
import {switchMap, tap, map} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import {Moment} from 'moment';
import {NgbTimeStruct} from '@ng-bootstrap/ng-bootstrap';
import {NotationService} from '../../../../shared/services/notation/notation.service';

@Component({
  selector: 'app-notations-modal',
  templateUrl: './notations-modal.component.html',
  styleUrls: ['./notations-modal.component.scss']
})
export class NotationsModalComponent implements OnInit {

  showSpinner: boolean = true;
  disabled: boolean = false;
  dateString: string;
  justification: string = this.notation.justification;
  locations: any[];
  datePicker: Date;
  timePicker: NgbTimeStruct;
  noteArray = ['A', 'B', 'C', 'D', 'E', 'F'];

  constructor(private route: ActivatedRoute,
              private notationService: NotationService,
              private matDialogRef: MatDialogRef<NotationsModalComponent>,
              private translateService: TranslateService,
              private toastr: ToastrService,
              @Inject(MAT_DIALOG_DATA) public notation
  ) {
  }

  ngOnInit() {
    this.justification = this.notation.justification || '';
    this.disabled = this.notation.action === 'read';
    this.locations = this.orderParentAndChildrenLocations(this.notation.project.project_device_locations);
    this.initNotationDate(this.notation.notedAt);
    this.getNotes(this.notation)
      .subscribe(
        (notes: any) => {
          // Forkjoin returns an array
          if (notes instanceof Array) {
            this.addAutomaticNotesToLocations(notes[0]);
            this.addNotesToLocations(notes[1]);
          } else {
            this.addNotesToLocations(notes);
          }
          this.showSpinner = false;
        },
        () => this.showSpinner = false
      );
  }

  updateDate(inputEvent: MatDatepickerInputEvent<Date>) {
    this.showSpinner = true;
    let dateString = this.formatDatesToString(this.timePicker, inputEvent.value, true);
    this.notationService.getAutomaticNotes(this.notation.projectId, dateString)
      .pipe(map((_) => [_]))
      .subscribe(
        (notes: any) => {
          this.clearAutomaticNotesToLocations();
          this.addAutomaticNotesToLocations(notes[0]);
          this.showSpinner = false;
        },
        (err) => this.showSpinner = false
      );
  }

  submit() {
    let notes = this.formatNotes(this.locations);
    let dates = this.formatDatesToString(this.timePicker, this.datePicker);
    this.showSpinner = true;
    (this.notation.noteId ?
      this.notationService.patchNote(this.notation.noteId, dates, this.justification, notes)
      : this.notationService.postNote(dates, this.justification, notes, this.notation.projectId))
      .pipe(
        switchMap(
          () => this.translateService.get('NOTES_ADD_SUCCESS')
        ),
        tap((message) => this.toastr.success(message))
      )
      .subscribe(
        () => {
          this.matDialogRef.close('success');
          this.showSpinner = false;
        },
        () => this.showSpinner = false
      );
  }

  private initNotationDate(notationDate: string = undefined) {
    let date = notationDate ? new Date(notationDate) : new Date();
    this.datePicker = date;
    this.timePicker = <NgbTimeStruct>{
      hour: notationDate ? date.getUTCHours() : date.getHours(),
      minute: notationDate ? date.getUTCMinutes() : date.getMinutes(),
      second: notationDate ? date.getUTCSeconds() : date.getSeconds()
    };
    this.dateString = moment(notationDate).utc(!notationDate).format('MMMM Do YYYY, h:mm:ss a');
  }

  private addNotesToLocations(notes = undefined) {
    this.locations.forEach(
      (location) => location.note = location.automaticNote ? 'auto' : 'ignore'
    );
    if (notes && notes.note_project_device_locations) {
      notes.note_project_device_locations.forEach(
        (note) => {
          if (!note.manu) {
            return;
          }
          let i = this.locations.findIndex(
            (location) => location.id === note.location_id
          );
          if (i > -1) {
            this.locations[i].note = note.note_litteral || 'ignore';
          }
        }
      );
    }
  }

  private addAutomaticNotesToLocations(autoNotes) {

    if (!autoNotes || !autoNotes.noteProject || !autoNotes.noteProject.noteLocation) {
      return;
    }

    autoNotes.noteProject.noteLocation.forEach(
      (note) => {
        let i = this.locations.findIndex(
          (location) => location.id === note.location
        );
        if (i > -1) {
          this.locations[i].automaticNote = note.noteLetter;
        }
      }
    );
  }

  private clearAutomaticNotesToLocations() {
    this.locations.forEach(
      (note) => note.automaticNote = undefined
    );
  }

  getNoteResults(location) {
    switch (location.note) {
      case 'ignore' :
        return '-*';
      case 'auto' :
        return location.automaticNote;
      case undefined :
        if (location.manu === true) {
          location.note = 'ignore';
        }
      default:
        return location.note + '*';
    }
  }

  private formatNotes(project) {
    return project.reduce(
      (locations, location) => {

        switch (location.note) {
          case 'ignore':
            return locations.concat([{
              project_device_location: location.id,
              note: null,
              manu: true
            }]);
          case 'auto': {
            if (location.automaticNote) {
              return locations.concat([{
                project_device_location: location.id,
                note: 5 - this.noteArray.findIndex(
                  (noteLitteral) => noteLitteral === location.automaticNote
                ),
                manu: false
              }]);
            } else {
              return locations;
            }
          }
          default:
            return locations.concat([{
              project_device_location: location.id,
              note: 5 - this.noteArray.findIndex(
                (noteLitteral) => noteLitteral === location.note
              ),
              manu: true
            }]);
        }

      }, []
    );
  }

  private formatDatesToString(time: NgbTimeStruct, date: Date | Moment, ISOFormat: boolean = false) {
    const dateAsDate: Date = date.constructor.name === 'Moment' ? moment(date).toDate() : date as Date;
    // Add '0' before number in case number has only one digit
    let formatTime = (value: number) => value < 10 ? '0' + value.toString() : value;
    if (ISOFormat) {
      return `${formatTime(dateAsDate.getFullYear())}-${formatTime(dateAsDate.getMonth() + 1)}-${formatTime(dateAsDate.getDate())}T${formatTime(time.hour)}:${formatTime(time.minute)}:${formatTime(time.second)}.000Z`;
    }
    return `${formatTime(dateAsDate.getFullYear())}:${formatTime(dateAsDate.getMonth() + 1)}:${formatTime(dateAsDate.getDate())} ${formatTime(time.hour)}:${formatTime(time.minute)}:${formatTime(time.second)}`;
  }

  private orderParentAndChildrenLocations(deviceLocations) {
    return deviceLocations.reduce(
      (devices, device, index, originalArray) => {
        if (device.parent) {
          return devices;
        }
        let children = device.children.map(
          (child) => originalArray.find(
            (location) => location.id === child.id || location.id === child.id
          )
        );
        return devices.concat(device).concat(children);
      }, []
    );
  }

  private getNotes(data) {
    switch (true) {
      case data.noteId && data.notedAt && data.aboMath:
        return forkJoin(
          this.notationService.getAutomaticNotes(this.notation.projectId, data.notedAt.split('+')[0] + 'Z'),
          this.notationService.getNotes(data.noteId)
        );
      case data.noteId && data.notedAt && true:
        return this.notationService.getNotes(data.noteId);
      case data.aboMath:
        return this.notationService.getAutomaticNotes(this.notation.projectId, moment().format().split('+')[0] + 'Z')
          .pipe(map((_) => [_]))
          ;
      default:
        return of([[], []]);
    }
  }
}

